// src/pages/ChatPage.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiSend, FiX, FiClock, FiUser } from 'react-icons/fi';
import OpenAI from 'openai';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
  onSnapshot,
  arrayUnion,
  Timestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import styles from '../styles/ChatPage.module.css';

const ChatPage = () => {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const messagesEndRef = useRef(null);

  // Chat & UI state
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [chatDocId, setChatDocId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Task-creation flow state (step-by-step)
  const [creationFlow, setCreationFlow] = useState(null);
  // creationFlow = { step: number, data: { taskName?, clientIds?, clientNames?, description?, startDate?, endDate?, priority?, assignedTo? } }

  // Ensure user is authenticated
  const currentUser = auth.currentUser;
  useEffect(() => {
    if (!currentUser) navigate('/login', { replace: true });
  }, [currentUser, navigate]);

  // Initialize OpenAI client
  const openai = new OpenAI({
    apiKey: process.env.REACT_APP_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true
  });

  // Initialize or load conversation and subscribe to realtime updates
  useEffect(() => {
    if (!currentUser) return;
    let unsubscribe = () => {};
    (async () => {
      try {
        let convRef;
        if (chatId && chatId !== 'new') {
          convRef = doc(db, 'users', currentUser.uid, 'conversations', chatId);
          const snap = await getDoc(convRef);
          if (!snap.exists()) throw new Error('Chat not found');
        } else {
          const col = collection(db, 'users', currentUser.uid, 'conversations');
          convRef = doc(col);
          await setDoc(convRef, {
            participants: [currentUser.uid],
            timestamp: serverTimestamp(),
            messages: [],
            status: 'active'
          });
          navigate(`/chat/${convRef.id}`, { replace: true });
        }
        setChatDocId(convRef.id);

        unsubscribe = onSnapshot(convRef, snap => {
          const data = snap.data();
          if (data?.messages) {
            setMessages(
              data.messages.map(m => ({
                ...m,
                timestamp: m.timestamp.toDate().toISOString()
              }))
            );
          }
        });
      } catch (err) {
        console.error('Chat init error:', err);
        navigate('/error', { state: { message: 'Failed to initialize chat' } });
      } finally {
        setLoading(false);
      }
    })();
    return () => unsubscribe();
  }, [chatId, currentUser, navigate]);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Monitor online/offline status
  useEffect(() => {
    const checkOnline = async () => {
      try {
        await fetch('https://www.google.com/', { mode: 'no-cors' });
        setIsOnline(true);
      } catch {
        setIsOnline(false);
      }
    };
    checkOnline();
    const id = setInterval(checkOnline, 30000);
    return () => clearInterval(id);
  }, []);

  // Firestore data fetch helpers
  const fetchUserTasks = useCallback(async () => {
    const q = query(
      collection(db, 'tasks'),
      where('assignedTo', 'array-contains', currentUser.uid)
    );
    const snap = await getDocs(q);
    const now = new Date();
    return snap.docs.map(d => {
      const data = d.data();
      const end = data.endDate?.toDate();
      return {
        id: d.id,
        ...data,
        endDate: end,
        isOverdue: end < now,
        daysOverdue: Math.floor((now - end) / (1000 * 60 * 60 * 24)),
        status: data.status || 'Not Started'
      };
    });
  }, [currentUser.uid]);

  const fetchUserClients = useCallback(async () => {
    const q = query(
      collection(db, 'clients'),
      where('ownerId', '==', currentUser.uid)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }, [currentUser.uid]);

  const fetchUserCampaigns = useCallback(async () => {
    const q = query(
      collection(db, 'campaigns'),
      where('ownerId', '==', currentUser.uid)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }, [currentUser.uid]);

  const fetchUserEmployees = useCallback(async () => {
    const snap = await getDocs(collection(db, 'users'));
    return snap.docs.map(d => ({
      id: d.id,
      name: d.data().name || d.data().email,
      email: d.data().email
    }));
  }, []);

  // “Tasks for today” logic
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfTomorrow = new Date(startOfToday);
  startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

  const handleTodayTasks = useCallback(async () => {
    const all = await fetchUserTasks();
    const today = all.filter(
      t => t.endDate >= startOfToday && t.endDate < startOfTomorrow
    );
    if (today.length === 0) return '🎉 You have no tasks due today.';
    const list = today
      .map(
        t =>
          `• ${t.taskName} (due ${t.endDate.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          })})`
      )
      .join('\n');
    return `✅ Your tasks for today:\n${list}`;
  }, [fetchUserTasks, startOfToday, startOfTomorrow]);

  // System prompt: expert marketing assistant
  const formatOpenAIMessages = useCallback(
    (text, { tasks, clients, campaigns }) => {
      const system = {
        role: 'system',
        content: `
+You are **Grow Up AI**, an expert marketing assistant. Provide actionable marketing strategy, best practices, and insights. Use the user's data below:
- ${tasks.length} tasks (${tasks.filter(t => t.isOverdue).length} overdue)
- ${clients.length} clients
- ${campaigns.length} campaigns
Frame responses with audience, channel, KPI context when relevant.`
      };
      const history = messages.slice(-6).map(m => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        content: m.message
      }));
      return [system, ...history, { role: 'user', content: text }];
    },
    [messages, currentUser.uid]
  );

  // Call OpenAI
  const getAIResponse = useCallback(
    async msgs => {
      try {
        const res = await openai.chat.completions.create({
          model: 'gpt-3.5-turbo',
          messages: msgs,
          max_tokens: 500
        });
        return res.choices[0].message.content;
      } catch (err) {
        console.error('OpenAI error:', err);
        return err.status === 429
          ? '😔 Quota exceeded—please try again shortly.'
          : "⚠️ I'm having trouble right now.";
      }
    },
    [openai]
  );

  // Append message to Firestore
  const appendMessageToFirestore = useCallback(
    async msg => {
      const ref = doc(
        db,
        'users',
        currentUser.uid,
        'conversations',
        chatDocId
      );
      await updateDoc(ref, {
        messages: arrayUnion({
          ...msg,
          timestamp: Timestamp.fromDate(new Date(msg.timestamp))
        }),
        timestamp: serverTimestamp()
      });
    },
    [chatDocId, currentUser.uid]
  );

  // Bot reply helper
  const addBotMessage = useCallback(
    async text => {
      const botMsg = {
        message: text,
        type: 'bot',
        timestamp: new Date().toISOString()
      };
      await appendMessageToFirestore(botMsg);
    },
    [appendMessageToFirestore]
  );

  // Create task in Firestore
  const createTaskInFirestore = useCallback(
    async ({
      taskName,
      clientIds,
      clientNames,
      description,
      startDate,
      endDate,
      priority,
      assignedTo
    }) => {
      await addDoc(collection(db, 'tasks'), {
        taskName,
        clientIds,
        clientNames,
        description,
        startDate: Timestamp.fromDate(new Date(startDate)),
        endDate: Timestamp.fromDate(new Date(endDate)),
        status: 'Not Started',
        assignedTo,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        priority,
        visibleTo: [currentUser.uid, ...assignedTo]
      });
    },
    [currentUser.uid]
  );

  // Step-by-step creation flow handler
  const handleCreationStep = useCallback(
    async input => {
      let { step, data } = creationFlow;
      let prompt;
      let next;

      if (step === 1) {
        data.taskName = input;
        prompt = '📋 Got it. Which client(s)? Please list exact names, comma-separated.';
        next = { step: 2, data };

      } else if (step === 2) {
        const allClients = await fetchUserClients();
        const names = input.split(',').map(s => s.trim().toLowerCase());
        const found = allClients.filter(c => names.includes(c.name.toLowerCase()));
        if (found.length !== names.length) {
          prompt = '❌ Some names didn’t match. Try again with exact client names.';
          next = { step: 2, data };
        } else {
          data.clientIds = found.map(c => c.id);
          data.clientNames = found.map(c => c.name);
          prompt = '✏️ Great. Now provide the description.';
          next = { step: 3, data };
        }

      } else if (step === 3) {
        data.description = input;
        prompt = '📅 What is the start date? (YYYY-MM-DD)';
        next = { step: 4, data };

      } else if (step === 4) {
        const d = new Date(input);
        if (isNaN(d)) {
          prompt = '❌ Invalid date. Use YYYY-MM-DD for the start date.';
          next = { step: 4, data };
        } else {
          data.startDate = input;
          prompt = '⏳ And the end date? (YYYY-MM-DD)';
          next = { step: 5, data };
        }

      } else if (step === 5) {
        const d = new Date(input);
        if (isNaN(d)) {
          prompt = '❌ Invalid end date. Use YYYY-MM-DD.';
          next = { step: 5, data };
        } else {
          data.endDate = input;
          prompt = '⚙️ What priority? Low, Medium (default), or High.';
          next = { step: 6, data };
        }

      } else if (step === 6) {
        const choice = input.trim().toLowerCase();
        if (!['low', 'medium', 'high', ''].includes(choice)) {
          prompt = '❌ Choose Low, Medium, or High.';
          next = { step: 6, data };
        } else {
          data.priority = choice
            ? choice.charAt(0).toUpperCase() + choice.slice(1)
            : 'Medium';
          prompt = '👥 Finally, who to assign? List employee names, comma-separated.';
          next = { step: 7, data };
        }

      } else if (step === 7) {
        const allEmps = await fetchUserEmployees();
        const names = input.split(',').map(s => s.trim().toLowerCase());
        const found = allEmps.filter(e => names.includes(e.name.toLowerCase()));
        if (found.length !== names.length) {
          prompt = '❌ Some employee names didn’t match. Try again.';
          next = { step: 7, data };
        } else {
          data.assignedTo = found.map(e => e.id);
          prompt = `🖋️ Creating task "${data.taskName}" now…`;
          next = null;
        }
      }

      await addBotMessage(prompt);
      if (next) {
        setCreationFlow(next);
      } else {
        await createTaskInFirestore(data);
        setCreationFlow(null);
        await addBotMessage(`🎉 Task "${data.taskName}" created successfully!`);
      }
    },
    [
      creationFlow,
      fetchUserClients,
      fetchUserEmployees,
      addBotMessage,
      createTaskInFirestore
    ]
  );

  // Main handler: quick-create or fallback to AI/step
  const generateAndSave = useCallback(
    async text => {
      setIsTyping(true);
      const lower = text.toLowerCase();

      // Quick-create
      if (/^create a task,/.test(lower)) {
        const parts = text.split(',').slice(1).map(p => p.trim());
        if (parts.length >= 7) {
          const [
            namePart,
            clientPart,
            descPart,
            startPart,
            endPart,
            priorityPart,
            assignPart
          ] = parts;

          const taskName = namePart.split('(')[0].trim();
          const description = descPart.split('(')[0].trim();

          const rawClient = clientPart.split('(')[0].trim();
          const allClients = await fetchUserClients();
          const clt = allClients.find(c => c.name.toLowerCase() === rawClient.toLowerCase()) || {};
          const clientIds = clt.id ? [clt.id] : [];
          const clientNames = clt.name ? [clt.name] : [rawClient];

          const rawAssignee = assignPart.split('(')[0].trim();
          const allEmps = await fetchUserEmployees();
          const emp = allEmps.find(e => e.name.toLowerCase() === rawAssignee.toLowerCase()) || {};
          const assignedTo = emp.id ? [emp.id] : [currentUser.uid];

          const rawPri = priorityPart.split('(')[0].trim().toLowerCase();
          const prio = ['low', 'medium', 'high'].includes(rawPri)
            ? rawPri.charAt(0).toUpperCase() + rawPri.slice(1)
            : 'Medium';

          const parseMDY = str => {
            const [m, d, y] = str.split('/').map(s => s.trim());
            return new Date(+y, +m - 1, +d).toISOString();
          };
          const startISO = parseMDY(startPart.split('(')[0]);
          const endISO = parseMDY(endPart.split('(')[0]);

          await createTaskInFirestore({
            taskName,
            clientIds,
            clientNames,
            description,
            startDate: startISO,
            endDate: endISO,
            priority: prio,
            assignedTo
          });

          await addBotMessage(
            `📝 Task “${taskName}” created successfully!\n\n` +
            `- Description: ${description}\n` +
            `- Client: ${clt.name || rawClient}\n` +
            `- Assignee: ${emp.name || rawAssignee}\n` +
            `- Start Date: ${startISO.slice(0,10)}\n` +
            `- End Date: ${endISO.slice(0,10)}\n` +
            `- Priority: ${prio}\n\n` +
            `🎉 All set!`
          );

          setIsTyping(false);
          return;
        }
      }

      // Step-by-step creation
      if (/^create task\b/.test(lower)) {
        await addBotMessage('🆕 Let’s create a task. What is the **Task Name**?');
        setCreationFlow({ step: 1, data: {} });
      } else if (creationFlow) {
        await handleCreationStep(text);
      } else if (lower.includes('task') && lower.includes('today')) {
        const reply = await handleTodayTasks();
        await addBotMessage(reply);
      } else {
        // Fallback to AI conversation
        try {
          const [tasks, clients, campaigns] = await Promise.all([
            fetchUserTasks(),
            fetchUserClients(),
            fetchUserCampaigns()
          ]);
          const msgs = formatOpenAIMessages(text, { tasks, clients, campaigns });
          const aiResp = await getAIResponse(msgs);
          await addBotMessage(aiResp);
        } catch {
          await addBotMessage('⚠️ Something went wrong generating a response.');
        }
      }

      setIsTyping(false);
    },
    [
      creationFlow,
      fetchUserClients,
      fetchUserEmployees,
      fetchUserTasks,
      fetchUserCampaigns,
      createTaskInFirestore,
      addBotMessage,
      handleCreationStep,
      handleTodayTasks,
      formatOpenAIMessages,
      getAIResponse,
      currentUser.uid
    ]
  );

  // Handle send button / enter key
  const handleSend = async () => {
    const text = userInput.trim();
    if (!text || isTyping || loading || !chatDocId || !isOnline) return;
    const userMsg = { message: text, type: 'user', timestamp: new Date().toISOString() };
    await appendMessageToFirestore(userMsg);
    setUserInput('');
    generateAndSave(text);
  };
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Initializing chat…</p>
      </div>
    );
  }

  return (
    <div className={styles.chatPageContainer}>
      {/* Header */}
      <div className={styles.chatHeader}>
        <button className={styles.historyButton} onClick={() => navigate('/chat/history')}>
          <FiClock /> المحفوظات
        </button>
        <h2>Grow Up AI</h2>
        <button className={styles.closeButton} onClick={() => navigate(-1)}>
          <FiX size={24} />
        </button>
      </div>

      {/* Messages */}
      <div className={styles.chatMessages}>
        {messages.map((m, i) => <ChatBubble key={i} msg={m} />)}
        {isTyping && (
          <div className={styles.typingIndicator}>
            <div /><div /><div /><span>جاري الكتابة...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className={styles.inputContainer}>
        <textarea
          value={userInput}
          onChange={e => setUserInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="اكتب رسالتك هنا..."
          dir={/[\u0600-\u06FF]/.test(userInput) ? 'rtl' : 'ltr'}
          disabled={!isOnline}
        />
        <button onClick={handleSend} disabled={!isOnline || isTyping} className={styles.sendButton}>
          <FiSend size={20} />
        </button>
      </div>
    </div>
  );
};

// ChatBubble subcomponent
const ChatBubble = ({ msg }) => {
  const date = new Date(msg.timestamp);
  const isArabic = /[\u0600-\u06FF]/.test(msg.message);
  return (
    <div className={`${styles.chatMessage} ${styles[msg.type]}`} dir={isArabic ? 'rtl' : 'ltr'}>
      <div className={styles.messageHeader}>
      {msg.type==='bot'
          ? <span className={styles.botLabel}>Grow Up AI</span>
          : <FiUser className={styles.userIcon} />}
        <span className={styles.messageTime}>
          {date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
      <div className={styles.messageContent}>
        {msg.message.split('\n').map((line, i) => <p key={i}>{line}</p>)}
      </div>
    </div>
  );
};

export default ChatPage;
